import { createSelector } from 'reselect';
import moment from 'moment-timezone';

import shipmentsSelectors from 'state/shipments/selectors';
import orderSelectors from 'state/order/selectors';
import { getExternalProps } from 'utils/selectors';
import {
  constants as dateAndTimeConstants,
  methods as dateAndTimeMethods,
  validators as dateAndTimeValidators,
} from 'utils/dateAndTimes';

import { isEmpty } from 'utils/types';

import { methods as addressesMethods } from 'utils/addresses';

const getShipmentsJoinedWithMaxTimes = createSelector(
  orderSelectors.getShipmentKeys,
  shipmentsSelectors.getState,
  (shipmentKeys, shipments) => {
    const allTimesInOrder = shipmentKeys.map((shipmentKey) => {
      const shipment = shipments[shipmentKey];

      const {
        pickup_date_from,
        pickup_date_to,
        pickup_time_from,
        pickup_time_to,

        dropoff_date_from,
        dropoff_date_to,
        dropoff_time_from,
        dropoff_time_to,
        address_from,
        address_to,
      } = shipment;

      return {
        pickup_date_from,
        pickup_date_to,
        pickup_time_from,
        pickup_time_to,

        dropoff_date_from,
        dropoff_date_to,
        dropoff_time_from,
        dropoff_time_to,

        timezonePickup: addressesMethods.getTimezoneOfAddress(address_from),
        timezoneDropoff: addressesMethods.getTimezoneOfAddress(address_to),
      };
    });

    const minMaxInOrder = allTimesInOrder.reduce(
      (acc, shipmentTimes) => ({
        pickup_time_from: shipmentTimes.pickup_time_from
          ? moment
              .max(
                dateAndTimeMethods.joinTimeAndDate(
                  acc.pickup_time_from,
                  acc.pickup_date_from,
                  shipmentTimes.timezonePickup
                ),
                dateAndTimeMethods.joinTimeAndDate(
                  shipmentTimes.pickup_time_from,
                  shipmentTimes.pickup_date_from,
                  shipmentTimes.timezonePickup
                )
              )
              .format('HH:mm')
          : acc.pickup_time_from,

        pickup_date_from: moment
          .max(
            dateAndTimeMethods.joinTimeAndDate(
              acc.pickup_time_from,
              acc.pickup_date_from,
              shipmentTimes.timezonePickup
            ),
            dateAndTimeMethods.joinTimeAndDate(
              shipmentTimes.pickup_time_from,
              shipmentTimes.pickup_date_from,
              shipmentTimes.timezonePickup
            )
          )
          .format('YYYY-MM-DD'),

        pickup_time_to: shipmentTimes.pickup_time_to
          ? moment
              .max(
                dateAndTimeMethods.joinTimeAndDate(
                  acc.pickup_time_to,
                  acc.pickup_date_to,
                  shipmentTimes.timezonePickup
                ),
                dateAndTimeMethods.joinTimeAndDate(
                  shipmentTimes.pickup_time_to,
                  shipmentTimes.pickup_date_to,
                  shipmentTimes.timezonePickup
                )
              )
              .format('HH:mm')
          : acc.pickup_time_to,

        pickup_date_to: moment
          .max(
            dateAndTimeMethods.joinTimeAndDate(
              acc.pickup_time_to,
              acc.pickup_date_to,
              shipmentTimes.timezonePickup
            ),
            dateAndTimeMethods.joinTimeAndDate(
              shipmentTimes.pickup_time_to,
              shipmentTimes.pickup_date_to,
              shipmentTimes.timezonePickup
            )
          )
          .format('YYYY-MM-DD'),

        dropoff_time_from: shipmentTimes.dropoff_time_from
          ? moment
              .max(
                dateAndTimeMethods.joinTimeAndDate(
                  acc.dropoff_time_from,
                  acc.dropoff_date_from,
                  shipmentTimes.timezoneDropoff
                ),
                dateAndTimeMethods.joinTimeAndDate(
                  shipmentTimes.dropoff_time_from,
                  shipmentTimes.dropoff_date_from,
                  shipmentTimes.timezoneDropoff
                )
              )
              .format('HH:mm')
          : acc.dropoff_time_from,

        dropoff_date_from: moment
          .max(
            dateAndTimeMethods.joinTimeAndDate(
              acc.dropoff_time_from,
              acc.dropoff_date_from,
              shipmentTimes.timezoneDropoff
            ),
            dateAndTimeMethods.joinTimeAndDate(
              shipmentTimes.dropoff_time_from,
              shipmentTimes.dropoff_date_from,
              shipmentTimes.timezoneDropoff
            )
          )
          .format('YYYY-MM-DD'),

        dropoff_time_to: shipmentTimes.dropoff_time_to
          ? moment
              .max(
                dateAndTimeMethods.joinTimeAndDate(
                  acc.dropoff_time_to,
                  acc.dropoff_date_to,
                  shipmentTimes.timezoneDropoff
                ),
                dateAndTimeMethods.joinTimeAndDate(
                  shipmentTimes.dropoff_time_to,
                  shipmentTimes.dropoff_date_to,
                  shipmentTimes.timezoneDropoff
                )
              )
              .format('HH:mm')
          : acc.dropoff_time_to,

        dropoff_date_to: moment
          .max(
            dateAndTimeMethods.joinTimeAndDate(
              acc.dropoff_time_to,
              acc.dropoff_date_to,
              shipmentTimes.timezoneDropoff
            ),
            dateAndTimeMethods.joinTimeAndDate(
              shipmentTimes.dropoff_time_to,
              shipmentTimes.dropoff_date_to,
              shipmentTimes.timezoneDropoff
            )
          )
          .format('YYYY-MM-DD'),
        pickup_timezone: acc.timezonePickup || moment.tz.guess(),
        dropoff_timezone: acc.timezoneDropoff || moment.tz.guess(),
      }),
      {
        ...allTimesInOrder[0],
      }
    );

    return minMaxInOrder;
  }
);

const getMinAndMaxForTimes = createSelector(
  shipmentsSelectors.getShipmentTimes,
  getShipmentsJoinedWithMaxTimes,
  getExternalProps.order,
  orderSelectors.maxNumberOfItemsInLoadsBetweenShipments,
  orderSelectors.getTimeWindowByShipmentKey,
  (
    shipmentTimes,
    shipmentsJoinedWithMaxTimes,
    { stopType, timeParam },
    maxNumberOfItemsInLoadsBetweenShipments,
    timeWindow
  ) => ({
    minMax: dateAndTimeValidators.getRulesWithRestrictions({
      times:
        stopType === dateAndTimeConstants.STOP_TYPES.pickup
          ? shipmentTimes
          : shipmentsJoinedWithMaxTimes,
      isForSuggestions: {
        dates: false,
        times: true,
      },
      restrictions: { maxNumberOfItemsInLoadsBetweenShipments },
      timeWindow,
    })[`${stopType}_${timeParam}`],
    date:
      stopType === dateAndTimeConstants.STOP_TYPES.pickup
        ? shipmentTimes[`${stopType}_date_from`]
        : shipmentsJoinedWithMaxTimes[`${stopType}_date_from`],
  })
);

const getOrderOfShipments = (state) => {
  const { mrCollection } = state;
  const shipmentsKeysInForm = mrCollection.data.shipments;

  const shipmentsOrder = [
    ...shipmentsKeysInForm.map((shipmentKey) => [
      shipmentKey,
      dateAndTimeConstants.STOP_TYPES.PICKUP,
    ]),
    ...shipmentsKeysInForm.map((shipmentKey) => [
      shipmentKey,
      dateAndTimeConstants.STOP_TYPES.DROPOFF,
    ]),
  ];
  return shipmentsOrder;
};

const getStop = ({ shipment, type }) => {
  const typeKey = type === 'pickup' ? 'from' : 'to';

  return {
    shipmentKey: shipment.shipmentKey,
    key: `${shipment.shipmentKey}.${type}`,
    stop_type: type,
    address: { ...shipment[`address_${typeKey}`] },
    time_from: dateAndTimeMethods.joinTimeAndDate(
      shipment[`${type}_time_from`],
      shipment[`${type}_date_from`],
      addressesMethods.getTimezoneOfAddress(
        shipment[addressesMethods.getAddressFromStopKey(type)]
      )
    ),
    time_to: dateAndTimeMethods.joinTimeAndDate(
      shipment[`${type}_time_to`],
      shipment[`${type}_date_to`],
      addressesMethods.getTimezoneOfAddress(
        shipment[addressesMethods.getAddressFromStopKey(type)]
      )
    ),

    loads: [...shipment.loads],
  };
};

const getStops = ({ shipments }) => {
  if (!isEmpty(shipments)) {
    const [firstShipment] = shipments;

    return [
      ...(shipments || []).map((shipment) => ({
        ...getStop({ shipment, type: 'pickup' }),
      })),
      {
        ...getStop({ shipment: firstShipment, type: 'dropoff' }),
        loads: (shipments || []).reduce(
          (acc, shipment) => [...acc, ...shipment.loads],
          []
        ),
      },
    ];
  }

  return [];
};

const getInsuredQuantity = () => false;

export default {
  getShipmentsJoinedWithMaxTimes,

  getMinAndMaxForTimes,

  getOrderOfShipments,

  getStops,

  getInsuredQuantity,
};
